import { capitalize } from "@prismicio/editor-support/String"
import { rawDragIndicatorIcon, tableFieldStyles } from "@prismicio/editor-ui"
import { type Editor } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { Selection } from "@tiptap/pm/state"
import { TableMap } from "@tiptap/pm/tables"
import clsx from "clsx"

import {
  createButtonElement,
  renderHtmlFromString,
} from "../../TableCell/utils"
import { TableEvent } from "../../TableEvent"
import { findTable, select } from "../../utils"

const tableHandleClassName = "data-prismic-table-handle"

interface NodePos {
  pos: number
  start: number
  node: ProseMirrorNode | null | undefined
}

export function getCellsInColumn(columnIndex: number | number[]) {
  return (selection: Selection) => {
    const table = findTable(selection)
    if (table) {
      const map = TableMap.get(table.node)
      const indexes = Array.isArray(columnIndex)
        ? columnIndex
        : Array.from([columnIndex])

      return indexes.reduce<NodePos[]>((acc, index) => {
        if (index >= 0 && index <= map.width - 1) {
          const cells = map.cellsInRect({
            left: index,
            right: index + 1,
            top: 0,
            bottom: map.height,
          })

          return acc.concat(
            cells.flatMap((nodePos) => {
              const node = table.node.nodeAt(nodePos)
              const pos = nodePos + table.start

              return { pos, start: pos + 1, node }
            }),
          )
        }

        return acc
      }, [])
    }
    return null
  }
}

// This function was extracted from https://templates.tiptap.dev.
// Permalink: https://github.com/ueberdosis/tiptap-templates/blob/b1147f20c4c28dc071a59f57d58b889b511db168/templates/next-block-editor-app/src/extensions/Table/utils.ts#L107
export function getCellsInRow(rowIndex: number | number[]) {
  return (selection: Selection) => {
    const table = findTable(selection)

    if (table) {
      const map = TableMap.get(table.node)
      const indexes = Array.isArray(rowIndex)
        ? rowIndex
        : Array.from([rowIndex])

      return indexes.reduce<NodePos[]>((acc, index) => {
        if (index >= 0 && index <= map.height - 1) {
          const cells = map.cellsInRect({
            left: 0,
            right: map.width,
            top: index,
            bottom: index + 1,
          })

          return acc.concat(
            cells.map((nodePos) => {
              const node = table.node.nodeAt(nodePos)
              const pos = nodePos + table.start
              return { pos, start: pos + 1, node }
            }),
          )
        }

        return acc
      }, [])
    }

    return null
  }
}

/** Finds the table handle element for the given type and element. */
export function findTableHandleElement(args: {
  type: "row" | "column"
  element: Element
}) {
  const { element, type } = args
  const handle = element.querySelector(`[${tableHandleClassName}="${type}"]`)
  if (!handle) throw new Error(`No ${type} handle found`)
  return handle
}

/** Creates a table handle element for the given type. */
export function createTableHandleElement(args: {
  type: "row" | "column"
  editor: Editor
  index: number
}) {
  const { type, editor, index } = args
  const button = createButtonElement({
    title: `Select ${type} & show options`,
    className: clsx(
      tableFieldStyles.tableHandle,
      tableFieldStyles[`table${capitalize(type)}Handle`],
    ),
    [tableHandleClassName]: type,
    "aria-expanded": false,
    onMouseDown: (event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      // Select all the cell in the row or column. Besides we wanting this
      // behavior, it's also needed for the delete action to work.
      editor.view.dispatch(select(type, { index, tr: editor.state.tr }))
    },
    onClick: (event, referenceElement) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      // Signal the corresponding floating menu to open.
      TableEvent.dispatch("handleClick", {
        type,
        editor,
        referenceElement,
      })
    },
  })

  const icon = renderHtmlFromString(rawDragIndicatorIcon)
  if (icon) button.appendChild(icon)
  return button
}
